import { useCallback } from "react";

import {
  useCommitChange,
  useField,
  useFieldContext,
  useFieldLabelAndDescription,
} from "@skilitics/designer-ui-components";
import type { VariableValue } from "@skilitics/nautilus-core";
import type { VariableConfig } from "@skilitics/nautilus-core-designer-data";

import type { VariableConfigDesignerProps } from "./VariableConfigDesigner";
import { VariableConfigDesigner } from "./VariableConfigDesigner";

type Props<T extends VariableValue> = Omit<
  VariableConfigDesignerProps<T>,
  "value" | "onChange" | "onChangeCommitted" | "label" | "helperText"
>;

export const FieldVariableConfig = <T extends VariableValue>(props: Props<T>) => {
  const commitChange = useCommitChange();

  const { dataTest, name } = useFieldContext();
  const [{ value }, _, { setValue }] = useField<VariableConfig<T> | undefined>(name);
  const [label, helperText] = useFieldLabelAndDescription(name);

  const handleChange: VariableConfigDesignerProps<T>["onChange"] = useCallback(
    (newValue) => setValue(newValue),
    [setValue],
  );

  return (
    <VariableConfigDesigner<T>
      {...props}
      helperText={helperText}
      label={label}
      onChange={handleChange}
      onChangeCommitted={commitChange}
      value={value}
      dataTest={dataTest}
    />
  );
};
